// Lagerplätze im Bestand (docs/kommissionieren/SPEZIFIKATION.md K4, K5): was auf welchem Platz liegt, Umbuchen von
// Platz zu Platz innerhalb eines Standorts und deren Storno. Alles nur mit der Funktion „Lagerplätze“ (`bins`).
// Die Plätze selbst (Kürzel, Bereich/Gang/Regal/Fach, Laufweg, Sperre) pflegt das Modul Stammdaten.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zLocationRef, zReason, zUnitInfo, zUnitQty } from './common'

/** Schlüssel des Funktionsschalters „Lagerplätze“ */
export const BINS_FEATURE = 'bins'

/** Ein Lagerplatz, wie Platzbestand und Kommissionierliste ihn zeigen. */
export const zBinInfo = z.object({
  id: zId,
  code: z.string(),
  name: z.string().nullable(),
  /** Art: pick, bulk, receiving, shipping, returns, blocked, empties */
  kind: z.string(),
  /** Reihenfolge im Laufweg */
  sort: z.int(),
  zone: z.string().nullable(),
  aisle: z.string().nullable(),
  rack: z.string().nullable(),
  shelf: z.string().nullable(),
  isDefault: z.boolean(),
  /** false = Sperrplatz (Sperrbestand) */
  isAvailable: z.boolean(),
  /** Zugriffssperre mit Grund: kein Vorschlag, keine Entnahme, kein Umbuchen auf den Platz */
  locked: z.boolean(),
  lockReason: z.string().nullable(),
  active: z.boolean(),
})
export type BinInfo = z.output<typeof zBinInfo>

export const zBinStockLine = z.object({
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  /** Zustand der Charge: ok, blocked, recall, expired */
  lotStatus: z.string().nullable(),
  /** Menge in Tausendstel der Basiseinheit */
  qty: z.int(),
  /** Lagergebinde des Artikels – für Anzeige und Umbuchen */
  unit: zUnitInfo,
  baseUnit: z.string(),
  /** „12 Ka + 4 Fl“ */
  qtyText: z.string(),
  /** Standardplatz dieses Artikels am Standort */
  isItemDefault: z.boolean(),
})

export const binStock = defineContract({
  name: 'inventory.bin.stock',
  title: 'Platzbestand anzeigen',
  description: 'Zeigt je Lagerplatz eines Standorts, welche Artikel und Warenposten darauf liegen – in der Reihenfolge des Laufwegs.',
  llm: {
    description:
      'Liefert die Lagerplätze eines Standorts in der Reihenfolge des Laufwegs mit dem Bestand darauf (Artikel, Warenposten ' +
      'mit Ablaufdatum, Menge in Tausendstel der Basiseinheit und als Text). Filter: ein Platz (`binId` oder `binCode`), ein Artikel, ' +
      'Bereich, Suchtext; `includeEmpty` nimmt leere Plätze mit. Verwenden für „Wo liegt …?“ und „Was liegt auf …?“.',
    keywords: ['Lagerplatz', 'Platzbestand', 'wo liegt', 'Regal', 'Fach', 'Stellplatz', 'Umbuchen'],
  },
  input: z.strictObject({
    locationId: zId,
    binId: zId.optional(),
    /** Kürzel bzw. gescanntes Etikett eines Platzes */
    binCode: z.string().trim().min(1).max(40).optional(),
    itemId: zId.optional(),
    zone: z.string().trim().max(40).optional(),
    q: z.string().trim().max(80).optional(),
    includeEmpty: z.boolean().default(false),
    limit: z.int().min(1).max(500).default(200),
  }),
  output: z.object({
    location: zLocationRef,
    bins: z.array(zBinInfo.extend({ lines: z.array(zBinStockLine) })),
    /** Bereiche des Standorts (für den Filter) */
    zones: z.array(z.string()),
    /** mehr Plätze, als `limit` erlaubt */
    truncated: z.boolean(),
  }),
  permission: 'inventory.stock.read',
  risk: 'read',
  scope: (input) => [input.locationId],
  feature: BINS_FEATURE,
})

export const binMove = defineContract({
  name: 'inventory.bin.move',
  title: 'Zwischen Lagerplätzen umbuchen',
  description: 'Bucht Ware innerhalb eines Standorts von einem Lagerplatz auf einen anderen – derselbe Warenposten, derselbe Wert.',
  llm: {
    description:
      'Bucht eine Menge eines Artikels (bei Rückverfolgung eines Warenpostens, `lotId`) von einem Lagerplatz auf einen ' +
      'anderen am selben Standort. `qty` im Gebinde `unitId` in Tausendstel (ohne `unitId` das Lagergebinde; 1 Karton = 1000). Ziel per ' +
      '`toBinId` oder `toBinCode` (Etikett). Nie auf einen gesperrten Platz; vom gesperrten Platz weg geht es (räumen). ' +
      'Der Wert ändert sich nicht. Rückgängig mit inventory.bin.moveReverse. Nicht für Umlagerungen zwischen Standorten.',
    keywords: ['umbuchen', 'Platz wechseln', 'umlagern im Lager', 'Nachschub Greifplatz', 'räumen', 'Lagerplatz'],
  },
  input: z.strictObject({
    locationId: zId,
    itemId: zId,
    fromBinId: zId,
    toBinId: zId.optional(),
    toBinCode: z.string().trim().min(1).max(40).optional(),
    /** Charge – Pflicht bei Artikeln mit Chargen */
    lotId: zId.nullish(),
    unitId: zId.nullish(),
    qty: zUnitQty,
    note: z.string().trim().max(500).nullish(),
  }),
  output: z.object({ postingId: zId, message: z.string() }),
  permission: 'inventory.bin.move',
  risk: 'write',
  scope: (input) => [input.locationId],
  effects: { reversible: true },
  feature: BINS_FEATURE,
})

export const zBinMoveRow = z.object({
  postingId: zId,
  at: z.date(),
  businessDate: z.string(),
  byName: z.string().nullable(),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  lotNo: z.string().nullable(),
  /** Menge in Tausendstel der Basiseinheit und als Text */
  qty: z.int(),
  qtyText: z.string(),
  from: z.object({ id: zId, code: z.string() }),
  to: z.object({ id: zId, code: z.string() }),
  note: z.string().nullable(),
  /** storniert (es gibt eine Gegenbuchung) */
  reversed: z.boolean(),
  /** diese Zeile IST die Gegenbuchung */
  isReversal: z.boolean(),
})

export const binMoves = defineContract({
  name: 'inventory.bin.moves',
  title: 'Umbuchungen anzeigen',
  description: 'Die letzten Umbuchungen zwischen Lagerplätzen eines Standorts, neueste zuerst.',
  llm: {
    description: 'Listet die letzten Umbuchungen zwischen Lagerplätzen eines Standorts (wer, wann, was, von wo nach wo, storniert ja/nein). Für den Storno die `postingId` an inventory.bin.moveReverse.',
    keywords: ['Umbuchungen', 'Platzwechsel', 'wer hat umgebucht'],
  },
  input: z.strictObject({ locationId: zId, limit: z.int().min(1).max(100).default(30) }),
  output: z.object({ rows: z.array(zBinMoveRow) }),
  permission: 'inventory.stock.read',
  risk: 'read',
  scope: (input) => [input.locationId],
  feature: BINS_FEATURE,
})

export const binMoveReverse = defineContract({
  name: 'inventory.bin.moveReverse',
  title: 'Umbuchung stornieren',
  description: 'Bucht eine Umbuchung zwischen Lagerplätzen per Gegenbuchung zurück.',
  llm: {
    description: 'Storniert eine Umbuchung (`postingId` aus inventory.bin.moves) mit Grund: Die Ware steht danach wieder auf dem alten Platz. Geht nur, solange sie auf dem neuen Platz noch liegt.',
    keywords: ['Umbuchung stornieren', 'zurückbuchen', 'rückgängig'],
  },
  input: z.strictObject({ postingId: zId, reason: zReason }),
  output: z.object({ postingId: zId, message: z.string() }),
  permission: 'inventory.bin.move',
  risk: 'write',
  scope: 'record',
  feature: BINS_FEATURE,
})

// ---- Kommissionierliste (Umlagerung) -----------------------------------------------------------------------------

/** Ein Teil des Vorschlags: aus diesem Platz diese Charge in dieser Menge (Tausendstel des Gebindes der Zeile). */
export const zPickPart = z.object({
  binId: zId.nullable(),
  /** '' ohne die Funktion „Lagerplätze“ */
  binCode: z.string(),
  binName: z.string().nullable(),
  /** Reihenfolge des Platzes im Laufweg */
  binSort: z.int(),
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  qty: z.int(),
})

/** Barcodes des Artikels: Code → Gebinde – der Ablauf am Telefon erkennt Scans ohne Rückfrage. */
export const zPickBarcode = z.object({ code: z.string(), unitId: zId, baseQty: z.int() })
